import type { DocumentField } from '@alpod-ai-writer/shared';
import { Avatar, AvatarGroup, Label, TextInput } from 'flowbite-react';
import { useCallback, useState } from 'react';
import * as Y from 'yjs';

import { getInitialsFromName } from '../../lib/identity.utils';
import { useDocumentEditor } from '../hooks/useDocumentEditor.hook';
import { useYDocAwarenessFieldSelection } from '../hooks/useYDocAwernessSelection.hook';
import { useYDocText } from '../hooks/useYDocText.hook';

export interface FieldEditorProps {
    yDoc: Y.Doc;
    field: DocumentField;
    readOnly: boolean;
}

export const FieldEditor: React.FC<FieldEditorProps> = ({
    yDoc,
    field,
    readOnly
}) => {
    const editor = useDocumentEditor();

    const [ value, setValue ] = useYDocText(yDoc, field.id);
    const [ isFocused, setIsFocused ] = useState(false);

    const {
        selectedBy,
        resetAwareness: resetAwerness,
        setAwarenessToField: setAwernessToField
    } = useYDocAwarenessFieldSelection(editor.yjsProvider, field.id);

    const handleFocus = useCallback(() => {
        setAwernessToField();
        setIsFocused(true);
    }, [ setAwernessToField ]);

    const handleBlur = useCallback(() => {
        resetAwerness();
        setIsFocused(false);
    }, [ resetAwerness ]);

    const selectionColor = selectedBy[0]?.color ?? undefined;

    return (
        <div>
            <Label htmlFor={`field-${field.id}`} className="block mb-1">{field.label}</Label>
            <div className='relative'>
                <TextInput
                    id={`field-${field.id}`}
                    type="text"
                    sizing={field.size ?? 'md'}
                    placeholder={field.placeholder}
                    required={field.isRequired}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    readOnly={readOnly}
                    style={selectionColor && !isFocused
                        ? {
                            outlineStyle: 'solid',
                            outlineColor: selectionColor,
                            outlineWidth: '2px',
                            outlineOffset: '-1px'
                        }
                        : undefined
                    }
                />
                <AvatarGroup className='absolute right-2 bottom-0 translate-y-1/2'>
                    {selectedBy.map((cursor) => (
                        <Avatar
                            key={cursor.clientId}
                            alt={cursor.name}
                            title={cursor.name}
                            placeholderInitials={getInitialsFromName(cursor.name)}
                            size='xs'
                            rounded
                            style={{
                                backgroundColor: cursor.color,
                                borderRadius: '1000px'
                            }}
                            theme={{
                                root: {
                                    base: 'relative',
                                    initials: {
                                        base: 'flex flex-row items-center justify-center bg-inherit',
                                        text: 'text-white text-xs'
                                    }
                                },
                            }}
                        />
                    ))}
                </AvatarGroup>
            </div>
        </div>
    );
};
